import { ReactDropdown } from '@storybook';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';

import { PipelineSettingsFormState } from 'global-stores';
import {
	EdgesState,
	NodesState,
} from 'views/complex-onboarding-flow';
import {
	AddedActionsState,
	AddedFileDataState,
	CurrentStepState,
} from 'views/onboarding-flow';
import { ComplexSettingFormState, useSendNotification } from '../store';
import { ReminderDate } from './reminder-date';
import { CheckboxInput } from './reminder-input/checkbox-input';

interface IOption {
	label: string;
	value: string | string[];
	price?: number;
}
interface ISelectComponent {
	label: string;
	name: string;
	selectOptions: IOption[];
	defaultValue: IOption;
	hasInviteModal?: boolean;
	id?: string;
}

let isHideCustom = '';

/** Breathing room kept between the menu and the bottom of the dialog. */
const MENU_GAP = 12;

export const SelectComponent: FC<ISelectComponent> = ({
	label,
	name,
	selectOptions,
	defaultValue,
	hasInviteModal,
	id,
}) => {
	const [settingsForm, setSettingsForm] = useRecoilState(
		PipelineSettingsFormState
	);
	const [currentStep, setCurrentStep] = useRecoilState<any>(CurrentStepState);
	const setAddedAccredationFile = useSetRecoilState(AddedFileDataState);
	const setAddedActions = useSetRecoilState(AddedActionsState);

	const [complexSettingForm, setComplexSettingForm] = useRecoilState(
		ComplexSettingFormState
	);

    const setNodes = useSetRecoilState(NodesState);
	const setEdges = useSetRecoilState(EdgesState);
	// hooks
	const { onChangeCheckbox, daysList } = useSendNotification();

	const updateActionPrice = useCallback((name: string, actionKey: string) => {
		setAddedActions(prev => {
			const prevState = JSON.parse(JSON.stringify(prev));
			const foundActionIndex = prevState.findIndex(
				({ key }: any) => key === actionKey
			);
			const foundOptionIndex = (
				prevState[foundActionIndex]?.metadata?.[0]?.options ?? []
			).findIndex((option: any) => option.value === name);
			const { price } =
				prevState[foundActionIndex]?.metadata?.[0]?.options?.[
					foundOptionIndex
				] ?? {};
			if (price) {
				prevState[foundActionIndex].price = price;
			}
			return prevState;
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onRemoveAccreditationBlock = useCallback(() => {
		setNodes((prev: any) => {
			const prevObj = JSON.parse(JSON.stringify(prev));
			const findIndex = prevObj.findIndex((el: any) => el.id === id);
			if (findIndex !== -1) {
				prevObj[findIndex].conditions = [];
			}
			return prevObj;
		});
		setEdges((prev: any) => {
			let prevObj = structuredClone(prev);
			prevObj = prevObj?.filter(
				(el: any) => !(el.source === id && el.source !== el.sourceHandle)
			);
			return prevObj;
		});
	}, [id, setEdges, setNodes]);

	const handleChange = useCallback(
		(event: any) => {
			if (name === 'signAgreementProvider') {
				updateActionPrice(event.value, 'signAgreementVerification');
			}
			if (event.value === '506c' && isHideCustom !== '506c') {
				setCurrentStep((preState: any) => ({
					...preState,
					copyMetadata: preState.metadata,
					metadata: [...preState.metadata].slice(0, 1),
				}));
				updateActionPrice('506c', 'accreditationVerify');
			} else if (event.value === '506b' && isHideCustom !== '506b') {
				onRemoveAccreditationBlock();
				setCurrentStep((preState: any) => ({
					...preState,
					metadata: preState.copyMetadata ?? preState.metadata,
				}));
				updateActionPrice('506b', 'accreditationVerify');
			}
			// eslint-disable-next-line
			isHideCustom = event.value;
			if (name === 'accreditationDocument') {
				setAddedAccredationFile(prev => ({ ...prev, type: event.value }));
			}

			if (id) {
				//we have created another state as there could be multiple kyc aml
				const payload = {
					[name]: event,
				};
				setSettingsForm({
					...settingsForm,
					accreditationDocument: { label: event.label, value: event.value },
				});

				setComplexSettingForm((prev: any) => {
					const newObj = structuredClone(prev);
					if (!newObj[id]) {
						newObj[id] = {};
					}

					if (newObj[id].accreditationDocument && event.value === '506c') {
						newObj[id].accreditationDocument = {
							label: 'Generic',
							value: 'generic',
						};
					}

					Object.assign(newObj[id], payload);
					return newObj;
				});
			}
		},
		[
			name,
			id,
			updateActionPrice,
			setCurrentStep,
			setAddedAccredationFile,
			setSettingsForm,
			settingsForm,
			setComplexSettingForm,
			onRemoveAccreditationBlock,
		]
	);

	// Setting default value for complex config
	useEffect(() => {
		if (id && !complexSettingForm?.[id]) {
			const payload = {
				[name]: defaultValue,
			};
			setComplexSettingForm((prev: any) => {
				const newObj = structuredClone(prev);
				if (!newObj[id]) {
					newObj[id] = {};
				}
				Object.assign(newObj[id], payload);
				return newObj;
			});
		}
	}, [complexSettingForm, defaultValue, id, name, setComplexSettingForm]);

	const initialValue = useMemo(() => {
		return complexSettingForm[id ?? '']?.[name];
	}, [complexSettingForm, id, name]);

	/* WHICH WAY THE MENU OPENS, MEASURED AGAINST THE DIALOG.
	 *
	 * The menu is portaled to <body> with fixed positioning so it escapes the two
	 * stacked scroll boxes that used to crop it. That left nothing to stop it
	 * overflowing the dialog instead: opened from a field near the bottom of the
	 * panel it covered the sticky "Total Cost" footer — hiding the cost while the
	 * provider that changes it is being chosen — and ran past the dialog's bottom
	 * edge onto the page behind. (SIM-41)
	 *
	 * react-select cannot work this out on its own. For a FIXED menu it measures
	 * against the VIEWPORT, so `getMenuPlacement` returns 'bottom' as soon as
	 * `viewSpaceBelow >= menuHeight` — the room below the DIALOG counts as room,
	 * which a short list always clears. `menuPlacement="auto"` changes nothing,
	 * because 'auto' and 'bottom' share that early return.
	 *
	 * POSITION IS NOT A PROXY FOR THIS, which a first pass here got wrong. Whether
	 * the provider select is the last field depends on the catalog: `PROVIDER_SELECTS`
	 * (routes-children/hooks/hook.ts) only PUSHES `kycProvider` when the step does
	 * not already carry it, so a deployment whose seed predates the feature renders
	 * it last, while a freshly seeded one has it second (steps-actions.json, index 1).
	 * The same field is at the bottom in one environment and near the top in another,
	 * so anything keyed on the field's identity is right in one and wrong in the other.
	 *
	 * Measuring the gap answers it in both: open downward when the list fits between
	 * the control and the bottom of the dialog, upward when it does not. Falls back
	 * to react-select's own behaviour if either element cannot be found.
	 *
	 * MEASURED BEFORE THE MENU OPENS, in the CAPTURE phase, and that is the whole
	 * trick. react-select decides placement in a REF CALLBACK on the menu element
	 * (`MenuPlacer.getPlacement`), so it reads `menuPlacement` exactly once, when the
	 * menu mounts, and never again — a ref callback is not re-invoked because a prop
	 * changed. Measuring in `onMenuOpen` therefore arrives a beat too late: the menu
	 * is already placed, the new value is stored but nothing re-reads it, and the
	 * first open goes the wrong way. Capture-phase mousedown runs before the
	 * control's own handler opens the menu, so React batches our placement update
	 * with react-select's `menuIsOpen`, and the menu mounts already knowing which way
	 * to go. Keyboard opens get the same treatment via keydown. */
	const rootRef = useRef<HTMLDivElement>(null);
	const [placement, setPlacement] = useState<'auto' | 'top' | 'bottom'>('auto');
	const [optionsDropHeight, setOptionsDropHeight] = useState<number | undefined>(undefined);

	const measurePlacement = useCallback(() => {
		const control =
			rootRef.current?.querySelector<HTMLElement>(
				'.portaled-dropdown__control, .dropdown__control, [class*="-control"]'
			) || rootRef.current;
		if (!control) return;
		const dialog = rootRef.current?.closest<HTMLElement>('.Modal__wrapper');
		const rect = control.getBoundingClientRect();
		const footer = dialog?.querySelector<HTMLElement>(
			'.ConfigureModal--footer, .ConfigureModals--footer, .ConfigureModal--footers'
		);
		const dialogRect = dialog?.getBoundingClientRect();
		const topLimit = dialogRect ? Math.max(dialogRect.top + 8, 8) : 8;
		const footerRect = footer?.getBoundingClientRect();
		const bottomLimit = footerRect
			? Math.min(footerRect.top, window.innerHeight) - MENU_GAP
			: (dialogRect ? Math.min(dialogRect.bottom, window.innerHeight) : window.innerHeight) - MENU_GAP;

		const spaceBelow = Math.max(0, bottomLimit - rect.bottom);
		const spaceAbove = Math.max(0, rect.top - topLimit);
		const numOptions = Math.max(1, selectOptions?.length || 1);
		const wanted = Math.min(300, numOptions * 46 + 10);

		if (spaceBelow >= wanted) {
			setPlacement('bottom');
			setOptionsDropHeight(wanted);
		} else if (spaceAbove >= wanted) {
			setPlacement('top');
			setOptionsDropHeight(wanted);
		} else if (spaceBelow >= spaceAbove) {
			setPlacement('bottom');
			setOptionsDropHeight(Math.max(60, spaceBelow));
		} else {
			setPlacement('top');
			setOptionsDropHeight(Math.max(60, spaceAbove));
		}
	}, [selectOptions?.length]);

	useEffect(() => {
		measurePlacement();
	}, [measurePlacement]);

	/** The keys react-select opens the menu on — measure before it does. */
	const measureOnOpenKey = useCallback(
		(event: React.KeyboardEvent) => {
			if ([' ', 'Enter', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
				measurePlacement();
			}
		},
		[measurePlacement]
	);

	return (
		<div
			className="select-component"
			key={name}
			ref={rootRef}
			onMouseDownCapture={measurePlacement}
			onTouchStartCapture={measurePlacement}
			onKeyDownCapture={measureOnOpenKey}
			onFocusCapture={measurePlacement}
		>
			{currentStep?.key !== 'reminderSetting' && (
				<div className="select-label">{label}</div>
			)}
			{currentStep?.key === 'reminderSetting' || hasInviteModal ? (
				<div className="reminder__container">
					<CheckboxInput
						label="Select specific date"
						onChangeCheckbox={onChangeCheckbox}
						checked={daysList.checked}
					/>

					<ReminderDate />
				</div>
			) : (
				<ReactDropdown
					/* The menu was being cropped. This select sits inside
					 * `.pipeline-settings__actions-details-container`, inside `.Modal__wrapper`.
					 * Portaling to <body> with fixed positioning escapes container clipping.
					 * Dynamically bounded placement and max height ensure it never overlaps
					 * the Total Cost footer or renders outside the dialog panel. */
					menuPortalTarget={document.body}
					menuPlacement={placement}
					optionsDropHeight={optionsDropHeight}
					optionsStyle={{
						'&:hover': { background: 'var(--color-hover-light-100)' },
					}}
					options={selectOptions}
					defaultValue={defaultValue}
					value={initialValue}
					handleChangeSelect={handleChange}
				/>
			)}
		</div>
	);
};
